"use client";
import * as m from "motion/react-m";
import { useEffect, useRef, useState } from "react";

export function Waveform({ seed, isLoading }: { isLoading?: boolean; seed: string }) {
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Calculate number of bars based on container width
  const barWidth = 2; // 2px bar width
  const gapWidth = 2; // 2px gap
  const totalBarWidth = barWidth + gapWidth;
  const barCount = containerWidth > 0 ? Math.floor(containerWidth / totalBarWidth) : 100;

  // Calculate 80% of container height for waveform range
  const maxWaveformHeight = containerHeight * 0.7;
  const minWaveformHeight = Math.max(4, containerHeight * 0.05); // Minimum 5% of container or 4px

  // Generate pseudo-random heights based on seed
  const generateHeights = (seed: string, count: number) => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    const heights: number[] = [];
    for (let i = 0; i < count; i++) {
      // Create more chaotic variation with multiple noise sources
      const noise1 = Math.sin(hash + i * 0.2) * 0.5 + 0.5;
      const noise2 = Math.sin(hash * 1.3 + i * 0.7) * 0.3;
      const noise3 = Math.sin(hash * 2.1 + i * 1.1) * 0.2;
      const noise4 = Math.sin(hash * 0.7 + i * 0.15) * 0.4;

      // Add sudden spikes and valleys for more realism
      const spike = Math.random() > 0.95 ? Math.random() * 0.8 : 0;
      const valley = Math.random() > 0.97 ? -Math.random() * 0.6 : 0;

      // Combine all noise sources for chaotic variation
      const combined = noise1 + noise2 + noise3 + noise4 + spike + valley;

      // Scale to use 80% of container height
      const heightRange = maxWaveformHeight - minWaveformHeight;
      const finalHeight = Math.max(
        minWaveformHeight,
        Math.min(maxWaveformHeight, combined * heightRange + minWaveformHeight),
      );
      heights.push(finalHeight);
    }
    return heights;
  };

  // Update container dimensions using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
        setContainerHeight(containerRef.current.offsetHeight);
      }
    };

    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    // Initial measurement
    updateDimensions();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const barHeights = containerHeight > 0 ? generateHeights(seed, barCount) : [];

  return (
    <div className='relative h-80 w-full rounded-lg border border-overlay-darker md:h-120' ref={containerRef}>
      {barHeights.map((height, index) => (
        <m.div
          animate={{
            height: isLoading ? 4 : height,
          }}
          className='absolute w-2 rounded-[1px] bg-overlay-darker-plus opacity-40'
          initial={{ height: 0 }}
          key={index}
          style={{
            left: `${index * totalBarWidth}px`,
            top: "50%",
            transform: "translateY(-50%)",
            width: `${barWidth}px`,
          }}
          transition={{
            type: "spring",
            stiffness: isLoading ? 300 : 200,
            damping: isLoading ? 20 : 10,
            delay: isLoading ? 0 : index * 0.005, // Stagger only when returning to original height
          }}
        />
      ))}
    </div>
  );
}
